import useSession from 'components/auth/useSession'
import { useInscricoesTopicosNotificacaoQuery } from 'graphql/hooks.generated'
import { TipoTopicoNotificacaoEnum } from 'graphql/types.generated'
import { usePubSub } from 'hooks/pubsub/usePubSub'
import { isEqual } from 'lodash'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import {
  InscricoesTopicosNotificacaoContextModel,
  NotificacaoCountContextModel,
  NotificacaoPubSubResponseList,
} from './model-notificacaoContext'
import {
  applyAtmosphereMessages,
  createDefaultCountTipoNotificacaoRecord,
  createDefaultInscricoesTopicosNotificacaoRecord,
  mapQueryToInscricoes,
  recomputeCounts,
} from './util-notificacaoContext'

const NotificacaoCountContext = createContext<NotificacaoCountContextModel>(createDefaultCountTipoNotificacaoRecord())
const InscricoesTopicosNotificacaoContext = createContext<InscricoesTopicosNotificacaoContextModel>(
  createDefaultInscricoesTopicosNotificacaoRecord()
)

interface NotificacaoProviderProps {
  children: React.ReactNode
}

export function NotificacaoProvider(props: Readonly<NotificacaoProviderProps>) {
  const { children } = props
  const [inscricoesTopicos, setInscricoesTopicos] = useState(createDefaultInscricoesTopicosNotificacaoRecord())
  const [notificacoes, setNotificacoes] = useState(createDefaultCountTipoNotificacaoRecord())

  const {
    data: { acesso },
  } = useSession()
  const tiposTopicosQuery = useMemo(() => Object.values(TipoTopicoNotificacaoEnum), [])
  const acessoId = acesso?.id

  const {
    data: { inscricoesTopicosNotificacao },
  } = useInscricoesTopicosNotificacaoQuery({
    variables: { input: { acessoId, tiposTopicos: tiposTopicosQuery } },
    context: { newRequest: true },
    pollInterval: 180000, // 3 minutos
    fetchPolicy: 'network-only',
    skip: !acessoId,
  })

  const inscricoesTopicosRef = useRef(inscricoesTopicos)
  const notificacoesRef = useRef(notificacoes)

  useEffect(() => {
    inscricoesTopicosRef.current = inscricoesTopicos
    notificacoesRef.current = notificacoes
  }, [inscricoesTopicos, notificacoes])

  useEffect(() => {
    if (!inscricoesTopicosNotificacao) return
    const next = mapQueryToInscricoes(inscricoesTopicosNotificacao)
    if (!isEqual(next, inscricoesTopicosRef.current)) {
      setInscricoesTopicos(next)
    }
  }, [inscricoesTopicosNotificacao])

  useEffect(() => {
    if (!inscricoesTopicos) return
    const next = recomputeCounts(inscricoesTopicos)
    if (!isEqual(next, notificacoesRef.current)) {
      setNotificacoes(next)
    }
  }, [inscricoesTopicos])

  const handleMessage = useCallback((mensagens: NotificacaoPubSubResponseList) => {
    if (!mensagens || mensagens.length === 0) return
    const base = inscricoesTopicosRef.current
    const next = applyAtmosphereMessages(base, mensagens)
    if (!isEqual(next, inscricoesTopicosRef.current)) {
      setInscricoesTopicos(next)
    }
  }, [])

  usePubSub<NotificacaoPubSubResponseList>({
    topic: `/notificacao/${acesso ? acesso.id : ''}`,
    onMessage: handleMessage,
  })

  return (
    <InscricoesTopicosNotificacaoContext.Provider value={inscricoesTopicos}>
      <NotificacaoCountContext.Provider value={notificacoes}>{children}</NotificacaoCountContext.Provider>
    </InscricoesTopicosNotificacaoContext.Provider>
  )
}

export const useInscricoesTopicosNotificacaoContext = () => useContext(InscricoesTopicosNotificacaoContext)
export const useNotificacaoCountContext = () => useContext(NotificacaoCountContext)
